import {
	startOfDay,
	endOfDay,
	startOfMonth,
	addMonths,
	subMonths,
	endOfMonth,
	startOfWeek,
	getWeek,
	getISOWeek,
} from 'date-fns';
import { CALENDAR_TYPES, LOCALE_CALENDAR_TYPES } from './const';

/**
 * Gets day of the week of a given date.
 * @param {Date} date Date.
 * @returns {number} Day of the week.
 */
export function getDayOfWeek(date: Date): number {
	return date.getDay();
}

/**
 * Week
 */

/**
 * Returns the beginning of a given week.
 *
 * @param {Date} date Date.
 * @returns {Date} Beginning of a given week.
 */
export function getBeginOfWeek(date: Date): Date {
	return startOfDay(startOfWeek(date));
}

/**
 * Gets week number according to ISO 8601 or US standard.
 * In ISO 8601, Arabic and Hebrew week 1 is the one with January 4.
 * In US calendar week 1 is the one with January 1.
 *
 * @param {Date} date Date.
 * @returns {number} Week number.
 */
export function getWeekNumber(date: Date): number {
	return getWeek(date);
}

/**
 * Others
 */

/**
 * Returns the beginning of a given range.
 *
 * @param {Date} date Date.
 * @returns {Date} Beginning of a given range.
 */
export function getBegin(date: Date): Date {
	return startOfDay(date);
}

/**
 * Returns the beginning of a previous given range.
 *
 * @param {Date} date Date.
 * @returns {Date} Beginning of a previous given range.
 */
export function getBeginPrevious(date: Date): Date {
	return startOfDay(startOfMonth(subMonths(date, 1)));
}

/**
 * Returns the beginning of a next given range.
 *
 * @param {Date} date Date.
 * @returns {Date} Beginning of a next given range.
 */
export function getBeginNext(date: Date): Date {
	return startOfDay(startOfMonth(addMonths(date, 1)));
}

export function getBeginPrevious2(date: Date): Date {
	return startOfDay(startOfMonth(subMonths(date, 12)));
}

export function getBeginNext2(date: Date): Date {
	return startOfDay(startOfMonth(addMonths(date, 12)));
}

/**
 * Returns the end of a given range.
 *
 * @param {Date} date Date.
 * @returns {Date} End of a given range.
 */
export function getEnd(date: Date): Date {
	return endOfDay(date);
}

/**
 * Returns the end of a previous given range.
 *
 * @param {RangeType} rangeType Range type (e.g. 'day')
 * @param {Date} date Date.
 * @returns {Date} End of a previous given range.
 */
export function getEndPrevious(date: Date): Date {
	return startOfDay(endOfMonth(subMonths(date, 1)));
}

export function getEndPrevious2(date: Date): Date {
	return startOfDay(endOfMonth(subMonths(date, 12)));
}

/**
 * Returns an array with the beginning and the end of a given range.
 *
 * @param {Date} date Date.
 * @returns {[Date, Date]} Beginning and end of a given range.
 */
export function getRange(date: Date): [Date, Date] {
	return [getBegin(date), getEnd(date)];
}

/**
 * Creates a range out of two values, ensuring they are in order and covering entire period ranges.
 *
 * @param {Date} date1 First date.
 * @param {Date} date2 Second date.
 * @returns {[Date, Date]} Beginning and end of a given range.
 */
export function getValueRange(date1: Date, date2: Date): [Date, Date] {
	const [start, end] = [date1, date2].sort(
		(a, b) => Math.floor(a.getTime() / 1000) - Math.floor(b.getTime() / 1000)
	) as [Date, Date];
	return [getBegin(start), getEnd(end)];
}

/**
 * Returns a boolean determining whether a given date is the current day of the week.
 *
 * @param {Date} date Date.
 * @returns {boolean} Whether a given date is the current day of the week.
 */
export function isCurrentDayOfWeek(date: Date): boolean {
	return date.getDay() === new Date().getDay();
}

/**
 * Returns a boolean determining whether a given date is a weekend day.
 *
 * @param {Date} date Date.
 * @returns {boolean} Whether a given date is a weekend day.
 */
export function isWeekend(date: Date, locale = 'en'): boolean {
	const calendarType = LOCALE_CALENDAR_TYPES[locale] ?? CALENDAR_TYPES.ISO_8601;
	const weekday = getISOWeek(date);

	switch (calendarType) {
		case CALENDAR_TYPES.ISLAMIC:
		case CALENDAR_TYPES.HEBREW:
			return weekday === 5 || weekday === 6;
		case CALENDAR_TYPES.ISO_8601:
		case CALENDAR_TYPES.GREGORY:
			return weekday === 6 || weekday === 7;
	}
	return false;
}
